// =====================================================================
// COMPONENT: grid.component.ts
// FUNCTIONAL OVERVIEW:
//   Renders reusable tabular data with sorting, pagination, filtering,
//   configurable cells, and row-level actions for application features.
// DEPENDENCIES: Angular Material table, paginator, sort, and menu controls
// ROUTE: Shared component embedded across application routes
// =====================================================================
import { Component, ElementRef, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
// import { GridConfig, ListDisplayedColumn } from '../../../model/shared/grid.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
// import { TooltipPipe } from '../../../pipes/shared/grid/tooltip.pipe';
// import { DEFAULT_GRID_CONFIG } from '../../../constants/shared/grid.constants';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { GridConfig, ListDisplayedColumn } from '../../../../models/shared/grid/grid.model';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  standalone:true,
  styleUrl: './grid.component.scss',
  imports:[
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSortModule,
    FormsModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatButtonModule
  ]
})
export class GridComponent {
  _config:GridConfig = {};
  _columns:ListDisplayedColumn[] = [];
  private currentPageSize = 5;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  dataSource = new MatTableDataSource();
  displayedColumns:string[] = [];

  sortingDataAccessor = (item: any, property: string): string | number => {
    const value = item[property];
    return typeof value === 'string' ? value.toLocaleLowerCase() : value;
  };

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChild('serialNumber', { static: true }) serialNumberTpl!: TemplateRef<any>;
  @ViewChild('prefixWithCustomKey', { static: true }) prefixWithCustomKeyTpl!: TemplateRef<any>;
  @ViewChild('hyperLinkDisplayStatic', { static: true }) hyperLinkDisplayStaticTpl!: TemplateRef<any>;
  @ViewChild('hyperLink', { static: true }) hyperLinkTpl!: TemplateRef<any>;
  @ViewChild('date', { static: true }) dateTpl!: TemplateRef<any>;
  @ViewChild('toggle', { static: true }) toggleTpl!: TemplateRef<any>;
  @ViewChild('edit_action', { static: true }) editActionTpl!: TemplateRef<any>;
  @ViewChild('delete_action', { static: true }) deleteActionTpl!: TemplateRef<any>;
  @ViewChild('chip_list', { static: true }) chipListTpl!: TemplateRef<any>;
  @ViewChild('menu_actions', { static: true }) menuActionsTpl!: TemplateRef<any>;
  @ViewChild('version_link', { static: true }) versionLinkTpl!: TemplateRef<any>;
  @ViewChild('status_badge', { static: true }) statusBadgeTpl!: TemplateRef<any>;
  @ViewChild('version_with_badge', { static: true }) versionWithBadgeTpl!: TemplateRef<any>;
  @ViewChild('link_action', { static: true }) linkActionTpl!: TemplateRef<any>;
  @ViewChild('chip_count', { static: true }) chipCountTpl!: TemplateRef<any>;
  @ViewChild('date_range', { static: true }) dateRangeTpl!: TemplateRef<any>;
  @ViewChild('job_status_badge', { static: true }) jobStatusBadgeTpl!: TemplateRef<any>;
  @ViewChild('status_action', { static: true }) statusActionTpl!: TemplateRef<any>;
  @ViewChild('default', { static: true }) defaultTpl!: TemplateRef<any>;

  @Input()
  set data(list:any){
    this.dataSource.data = list;
  }

  @Input()
  set columns(columns:ListDisplayedColumn[]){
    this._columns = columns;
    this.displayedColumns = columns.map((column:ListDisplayedColumn)=>column.key)
  };

  @Input()
  set config(config:GridConfig){
    this._config = config;
    if (config.rowClickable) {
      this.updateCellClassToClickable()
    }
  }

  @Input()
  set search(searchValue:string){
    this.applyFilter(searchValue)
  }

  @Output()
  rowClick = new EventEmitter()

  @Output()
  cellClick = new EventEmitter()

  @Output()
  searchResultStatus = new EventEmitter()

  @Output()
  sortChange = new EventEmitter()

  @Output()
  statusToggleChange = new EventEmitter()

  /** Emitted by the `menu_actions` cell type when a menu item is clicked. */
  @Output()
  rowAction = new EventEmitter<{ action: string; element: any }>()

  ngAfterViewInit(){
    this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onCellClick(element:any){
    this.cellClick.emit(element);
  }

  applyFilter(searchValue:string){
    this.dataSource.filter = searchValue.trim().toLowerCase();

    if (this.dataSource.filteredData.length == 0 && searchValue.length > 0) {
      this.searchResultStatus.emit(false);
    } else {
      this.searchResultStatus.emit(true);
    }
  }

  updateCellClassToClickable(){
    this._columns.forEach((column:ListDisplayedColumn)=>{
      column.cell_class = column.cell_class ? `${column.cell_class} cell_clickable` : 'cell_clickable';
    })
  }

  sortData(event:Sort){
    this.sortChange.emit(event);
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Keeps the resized grid in view after the user changes how many
  //   rows are displayed, without affecting regular page navigation.
  //
  // MODIFICATION HISTORY (latest first - keep ONLY the last 3):
  //   [JHUD-5664] 2026-09-30 18:41 | Vikas Kumar | Prevented page-end scrolling after reducing the page size.
  // -----------------------------------------------------------------
  /**
   * Restores the grid viewport after a paginator page-size change.
   * @param event Current Angular Material paginator state.
   * @returns Nothing.
   */
  onPageChange(event: PageEvent): void {
    const pageSizeChanged = event.pageSize !== this.currentPageSize;
    this.currentPageSize = event.pageSize;

    if (!pageSizeChanged) {
      return;
    }

    requestAnimationFrame(() => {
      this.elementRef.nativeElement.scrollIntoView({ block: 'start' });
    });
  }

  getCellTemplate(type: string): TemplateRef<any> {
    switch (type) {
      case 'serial_number': return this.serialNumberTpl;
      case 'prefix_with_custom_key': return this.prefixWithCustomKeyTpl;
      case 'hyper_link_display_static': return this.hyperLinkDisplayStaticTpl;
      case 'hyper_link': return this.hyperLinkTpl;
      case 'date': return this.dateTpl;
      case 'toggle': return this.toggleTpl;
      case 'edit_action': return this.editActionTpl;
      case 'delete_action': return this.deleteActionTpl;
      case 'chip_list': return this.chipListTpl;
      case 'menu_actions': return this.menuActionsTpl;
      case 'version_link': return this.versionLinkTpl;
      case 'status_badge': return this.statusBadgeTpl;
      case 'version_with_badge': return this.versionWithBadgeTpl;
      case 'link_action': return this.linkActionTpl;
      case 'chip_count': return this.chipCountTpl;
      case 'date_range': return this.dateRangeTpl;
      case 'job_status_badge': return this.jobStatusBadgeTpl;
      case 'status_action': return this.statusActionTpl;
      default: return this.defaultTpl;
    }
  }

  onToggleChange(element, column){
    this.statusToggleChange.emit(element);
  }

  onMenuAction(action: string, element: any){
    this.rowAction.emit({ action, element });
  }

  isMenuItemVisible(item: any, element: any): boolean {
    return !item.hide_when || !item.hide_when(element);
  }
}
